import React, { useState } from 'react';
import {
  Target, DollarSign, Clock, Users, ChevronDown, ChevronUp
} from 'lucide-react';
import './DispatchGoalPanel.css';

// Clamp a percentage for the visual bar fill (0–100). The numeric label can
// still read above 100% to show we've beaten a target.
const clampFill = (pct) => Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0));
const fmtPct = (pct) => `${Number.isFinite(pct) ? Math.round(pct) : 0}%`;
const fmtHours = (h) => `${Math.round((Number.isFinite(h) ? h : 0) * 10) / 10}h`;
const fmtMoney = (n) => `$${Math.round(Number.isFinite(n) ? n : 0).toLocaleString()}`;

// Pick a tone for a metric where higher-is-better toward 100% of goal.
const toneForGoal = (pct) => {
  if (pct >= 100) return 'good';
  if (pct >= 80) return 'ok';
  return 'low';
};

function GoalBar({ pct, tone }) {
  return (
    <div className="goal-bar-track">
      <div
        className={`goal-bar-fill goal-tone-${tone}`}
        style={{ width: `${clampFill(pct)}%` }}
      />
    </div>
  );
}

const GOAL_PANEL_STORAGE_KEY = 'dispatch.goalPanel.collapsed';

function DispatchGoalPanel({
  revenue,
  completedRevenue,
  dailyRevenueTarget,
  pctToTarget,
  pctBooked,
  revenuePerStaff,
  pctRevenuePerStaff,
  girlsScheduled,
  girlsNeeded,
  pctStaffing,
  goalHours,
  scheduledHours,
  actualClockedHours,
  pctHoursToGoal,
  pctHoursVsScheduled,
  revenueGoalPerStaff,
  hoursPerStaffDay,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(GOAL_PANEL_STORAGE_KEY) === '1'; }
    catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(GOAL_PANEL_STORAGE_KEY, next ? '1' : '0'); } catch (e) { /* noop */ }
      return next;
    });
  };

  const headlineTone = toneForGoal(pctToTarget);

  // Staffing tone: green when fully staffed to need; amber when over- or under-staffed.
  const staffingTone = girlsNeeded === 0
    ? 'good'
    : girlsScheduled === girlsNeeded
      ? 'good'
      : girlsScheduled > girlsNeeded
        ? 'over'
        : 'low';

  return (
    <div className={`dispatch-goal-panel ${collapsed ? 'is-collapsed' : ''}`}>
      <button
        type="button"
        className="goal-panel-header"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        title={collapsed ? 'Expand daily goal tracker' : 'Collapse daily goal tracker'}
      >
        <Target size={15} className="goal-panel-header-icon" />
        <span className="goal-panel-title">Daily Goal Tracker</span>
        <span className="goal-panel-goalnote">
          {fmtMoney(revenueGoalPerStaff)}/girl · {hoursPerStaffDay}h day
        </span>
        {collapsed && (
          <span className={`goal-panel-mini goal-tone-text-${headlineTone}`}>
            {fmtPct(pctToTarget)} to target
          </span>
        )}
        <span className="goal-panel-chevron">
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </span>
      </button>

      {!collapsed && (
        <div className="goal-panel-body">
          {/* Headline: % to daily revenue target (live, climbs as jobs complete) */}
          <div className="goal-headline">
            <div className="goal-headline-top">
              <DollarSign size={16} className="goal-headline-icon" />
              <span className="goal-headline-label">% to Daily Revenue Target</span>
              <span className={`goal-headline-pct goal-tone-text-${headlineTone}`}>
                {fmtPct(pctToTarget)}
              </span>
            </div>
            <GoalBar pct={pctToTarget} tone={headlineTone} />
            <div className="goal-headline-sub">
              <span>
                <strong>{fmtMoney(completedRevenue)}</strong> earned of{' '}
                <strong>{fmtMoney(dailyRevenueTarget)}</strong> target
              </span>
              <span className="goal-sub-muted">
                Booked: {fmtMoney(revenue)} ({fmtPct(pctBooked)})
              </span>
            </div>
          </div>

          <div className="goal-rows">
            {/* Revenue per girl */}
            <div className="goal-row">
              <div className="goal-row-head">
                <DollarSign size={14} className="goal-row-icon" />
                <span className="goal-row-label">Revenue / Girl</span>
                <span className={`goal-row-pct goal-tone-text-${toneForGoal(pctRevenuePerStaff)}`}>
                  {fmtPct(pctRevenuePerStaff)}
                </span>
              </div>
              <GoalBar pct={pctRevenuePerStaff} tone={toneForGoal(pctRevenuePerStaff)} />
              <div className="goal-row-values">
                <strong>{fmtMoney(revenuePerStaff)}</strong> of {fmtMoney(revenueGoalPerStaff)} goal
              </div>
            </div>

            {/* Hours */}
            <div className="goal-row">
              <div className="goal-row-head">
                <Clock size={14} className="goal-row-icon" />
                <span className="goal-row-label">Hours</span>
                <span className={`goal-row-pct goal-tone-text-${toneForGoal(pctHoursToGoal)}`}>
                  {fmtPct(pctHoursToGoal)}
                </span>
              </div>
              <GoalBar pct={pctHoursToGoal} tone={toneForGoal(pctHoursToGoal)} />
              <div className="goal-row-values">
                <strong>{fmtHours(actualClockedHours)}</strong> clocked of {fmtHours(goalHours)} goal
                <span className="goal-sub-muted">
                  {' '}· vs scheduled {fmtHours(scheduledHours)} ({fmtPct(pctHoursVsScheduled)})
                </span>
              </div>
            </div>

            {/* Staffing */}
            <div className="goal-row">
              <div className="goal-row-head">
                <Users size={14} className="goal-row-icon" />
                <span className="goal-row-label">Staffing</span>
                <span className={`goal-row-pct goal-tone-text-${staffingTone}`}>
                  {fmtPct(pctStaffing)}
                </span>
              </div>
              <GoalBar pct={pctStaffing} tone={staffingTone} />
              <div className="goal-row-values">
                <strong>{girlsScheduled}</strong> scheduled of <strong>{girlsNeeded}</strong> needed
                {girlsScheduled > girlsNeeded && girlsNeeded > 0 && (
                  <span className="goal-sub-muted"> · overstaffed</span>
                )}
                {girlsScheduled < girlsNeeded && (
                  <span className="goal-sub-muted"> · short {girlsNeeded - girlsScheduled}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DispatchGoalPanel;
